import { useCallback, useEffect, useMemo, useState } from "react";
import { InvoicePdfViewer } from "../components/InvoicePdfViewer";
import { shelfZonesOnly } from "../utils/shelfZones";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/apiClient";
import { DuplicateNameDialog } from "../components/DuplicateNameDialog";
import type {
  CatalogDraft,
  CatalogItem,
  Color,
  InvoiceDetail,
  InvoiceLineRow,
  InvoiceMoneySource,
  InvoicePreviewDetail,
  InvoicePreviewLine,
  ExistingItemRef,
  ItemCategory,
  Location,
} from "../api/types";
import type { DraftRowPatch } from "../components/catalog/DraftReviewRow";
import { InvoiceParserReport } from "../components/InvoiceParserReport";
import { HandTypedPurchasesHint } from "../components/invoices/HandTypedPurchasesHint";
import { InvoicePaymentsBlock } from "../components/invoices/InvoicePaymentsBlock";
import { PaperUseBlock } from "../components/invoices/PaperUseBlock";
import { FacturaDetailsBlock } from "../components/invoices/FacturaDetailsBlock";
import { PaperCoverageBlock } from "../components/invoices/PaperCoverageBlock";
import { CreditLinkBlock } from "../components/invoices/CreditLinkBlock";
import { DeletePaperButton } from "../components/invoices/DeletePaperButton";
import { PaperOpenLinesTable, type LineLink } from "../components/invoices/PaperOpenLinesTable";
import { PaperLinkedLinesList } from "../components/invoices/PaperLinkedLinesList";
import { calendarDayText, cost, money, percentText } from "../i18n/intl";
import { useAuth } from "../state/AuthContext";
import { canSchedule, isBill, isCreditNote, paperUseLabel } from "../utils/paperUse";
import { showsRateRows } from "../utils/vatBreakdownRows";

function nowLocalIsoMinute(): string {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 24);
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
}

/**
 * G-121 S1: what the paper prints beside a line's net figures — its IVA
 * rate, its gross amount, a discount off a list price — each appended only
 * when the parser read it. An unknown is left out, never shown as 0.
 */
function lineMoneyExtras(line: InvoiceLineRow): string {
  const parts: string[] = [];
  if (line.ivaRatePercent != null) {
    parts.push(`IVA ${percentText(line.ivaRatePercent)}%`);
  }
  if (line.lineGrossEur != null) {
    parts.push(`gross ${money(line.lineGrossEur)}`);
  }
  if (line.discountPercent != null) {
    parts.push(
      line.listUnitPriceEur != null
        ? `${percentText(line.discountPercent)}% off list ${cost(line.listUnitPriceEur)}`
        : `${percentText(line.discountPercent)}% off`,
    );
  } else if (line.listUnitPriceEur != null) {
    parts.push(`list ${cost(line.listUnitPriceEur)}`);
  }
  return parts.map((part) => ` · ${part}`).join("");
}

/**
 * G-121 S1: where the header money came from, when that is worth saying.
 * PRINTED needs no note — it is the default claim of the block. Null for
 * an invoice with no recorded source at all.
 */
function moneySourceNote(source: InvoiceMoneySource | null): string | null {
  if (source === "BACKFILLED_FROM_AUDIT") {
    return "From the parser's earlier audit, not re-read from the paper";
  }
  if (source === "DERIVED") {
    return "IVA is total − base; the paper printed no IVA line";
  }
  // G-164: typed on the "Factura" block below, the total worked out.
  if (source === "TYPED") {
    return "Typed by a person; total = base + IVA";
  }
  return null;
}

type OpenLine = { line: InvoicePreviewLine; draft: CatalogDraft };

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // G-121 S4 (OD-147): a factura's payments are the owner's alone.
  const { hasRole } = useAuth();
  const isOwner = hasRole("OWNER");

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [preview, setPreview] = useState<InvoicePreviewDetail | null>(null);
  /** G-173: bumped when a credit note is linked or unlinked — the payment block re-reads "to pay". */
  const [creditTick, setCreditTick] = useState(0);
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [locationDetail, setLocationDetail] = useState<Location | null>(null);
  // G-129: per line, the item and zone as the person has them — the stored
  // link, else the matcher's find, else a pick not yet saved.
  const [links, setLinks] = useState<Record<string, LineLink>>({});
  const [busyLineIds, setBusyLineIds] = useState<Set<string>>(() => new Set());
  const [createAllBusy, setCreateAllBusy] = useState(false);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});

  const [scheduledFor, setScheduledFor] = useState(nowLocalIsoMinute);
  const [defaultZoneId, setDefaultZoneId] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdf, setPdf] = useState<{ invoiceId: string; url?: string; error?: string } | null>(null);
  const pdfUrl = pdf?.invoiceId === id ? pdf?.url : undefined;
  const pdfError = pdf?.invoiceId === id ? pdf?.error : undefined;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let objectUrl: string | undefined;
    setPdf(null);
    void api.getInvoicePdf(id).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setPdf({ invoiceId: id, url: objectUrl });
    }).catch((err: unknown) => {
      if (!cancelled) {
        setPdf({ invoiceId: id, error: err instanceof Error ? err.message : "Could not load PDF" });
      }
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);
  const [saveBusy, setSaveBusy] = useState(false);
  const [commitBusy, setCommitBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const applyPreview = useCallback((p: InvoicePreviewDetail) => {
    setPreview(p);
    // The server's link wins; a pick made here and not yet saved survives a
    // refresh (creating one line's item must not drop the zone chosen on another).
    setLinks((prev) => {
      const d: Record<string, LineLink> = {};
      for (const ln of p.lines) {
        d[ln.id] = {
          itemId: ln.itemId ?? prev[ln.id]?.itemId ?? ln.matchHints?.itemId ?? "",
          zoneId: ln.zoneId ?? prev[ln.id]?.zoneId ?? "",
        };
      }
      return d;
    });
  }, []);

  const refreshAll = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!id) return null;
      if (!opts?.silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const inv = await api.getInvoice(id);
        setInvoice(inv);
        if (inv.status === "PARSED" || inv.status === "NEEDS_REVIEW") {
          const p = await api.getInvoicePreview(id);
          applyPreview(p);
        } else {
          setPreview(null);
          setLinks({});
        }
        if (!opts?.silent) setError(null);
        return inv;
      } catch (err: unknown) {
        if (!opts?.silent) {
          setInvoice(null);
          setPreview(null);
          setError(
            err instanceof Error ? err.message : "Could not load invoice",
          );
        }
        return null;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [id, applyPreview],
  );

  const reloadItems = useCallback(async () => {
    try {
      setItems(await api.listItems({ includeInactive: true }));
    } catch {
      // The list is a convenience for labels; a failed reload shows ids.
    }
  }, []);

  const shouldPollInvoice =
    invoice != null &&
    (invoice.status === "UPLOADED" || invoice.status === "PARSING");

  const catalogLocationId: string | null =
    invoice &&
    (invoice.status === "PARSED" ||
      invoice.status === "NEEDS_REVIEW" ||
      invoice.status === "COMMITTED")
      ? invoice.locationId
      : null;

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!id || !shouldPollInvoice) return;
    const t = window.setInterval(() => {
      void refreshAll({ silent: true });
    }, 3000);
    return () => window.clearInterval(t);
  }, [id, shouldPollInvoice, refreshAll]);

  useEffect(() => {
    if (!catalogLocationId) return;

    let cancelled = false;
    void Promise.all([
      api.listItems({ includeInactive: true }).then((rows) => {
        if (!cancelled) setItems(rows);
      }),
      api.getLocation(catalogLocationId).then((loc) => {
        if (!cancelled) setLocationDetail(loc);
      }),
      api.listCategories().then((cats) => {
        if (!cancelled) setCategories(cats);
      }),
      api.listColors().then((rows) => {
        if (!cancelled) setColors(rows);
      }),
    ]).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [catalogLocationId]);

  const itemsSorted = useMemo(
    () => [...items].sort((a, b) => a.name.localeCompare(b.name)),
    [items],
  );

  const categoriesSorted = useMemo(
    () => [...categories].sort((a, b) => a.label.localeCompare(b.label)),
    [categories],
  );

  const zonesSorted = useMemo(() => {
    if (!locationDetail?.zones?.length) return [];
    // G-84: boxes are not receiving zones. A delivery lands on a shelf; it
    // reaches a box only when someone scans that box's label.
    return shelfZonesOnly([...locationDetail.zones]).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [locationDetail]);

  const itemLabel = useCallback(
    (itemId: string | null | undefined) => {
      if (!itemId) return "—";
      const hit = itemsSorted.find((i) => i.id === itemId);
      return hit ? `${hit.name}${hit.sku ? ` (${hit.sku})` : ""}` : itemId;
    },
    [itemsSorted],
  );

  const zoneLabel = useCallback(
    (zoneId: string | null | undefined) => {
      if (!zoneId) return "—";
      const hit = zonesSorted.find((z) => z.id === zoneId);
      return hit?.name ?? zoneId;
    },
    [zonesSorted],
  );

  const setLink = useCallback(
    (lineId: string, patch: Partial<LineLink>) =>
      setLinks((prev) => ({
        ...prev,
        [lineId]: { ...(prev[lineId] ?? { itemId: "", zoneId: "" }), ...patch },
      })),
    [],
  );

  const markBusy = useCallback((lineIds: string[], on: boolean) => {
    setBusyLineIds((prev) => {
      const next = new Set(prev);
      for (const lineId of lineIds) {
        if (on) next.add(lineId);
        else next.delete(lineId);
      }
      return next;
    });
  }, []);

  /** A draft edited in the row comes back from the server and replaces itself on its line. */
  const replaceDraft = useCallback((updated: CatalogDraft) => {
    setPreview((p) =>
      p
        ? { ...p, lines: p.lines.map((l) => (l.draft?.id === updated.id ? { ...l, draft: updated } : l)) }
        : p,
    );
  }, []);

  const patchDraft = async (draft: CatalogDraft, patch: DraftRowPatch) => {
    try {
      replaceDraft(await api.updateCatalogDraft(draft.id, patch));
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "Could not save the draft");
    }
  };

  const photoDraft = async (draftId: string, file: File | null) => {
    if (!file) return;
    try {
      replaceDraft(await api.setCatalogDraftPhoto(draftId, file));
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "Could not save the photo");
    }
  };

  // G-119: a line whose Create & link the server held back because an active
  // item already carries the name — one line's question, or the batch's.
  const [duplicatePrompt, setDuplicatePrompt] = useState<
    (OpenLine & { existing: ExistingItemRef[] }) | null
  >(null);
  const [duplicateBatch, setDuplicateBatch] = useState<
    Array<OpenLine & { existing: ExistingItemRef[] }>
  >([]);

  /**
   * G-129 (OD-159): one tap — the SAME finalize-many pass the staging page
   * runs (photo adopted, barcode, supplier alias, undo), applying the
   * suggestion where the person left the field blank; the server links the
   * paper line in the same transaction.
   */
  const createAndLink = async (
    line: InvoicePreviewLine,
    draft: CatalogDraft,
    confirmDuplicateOf?: string[],
  ) => {
    setLineErrors((p) => ({ ...p, [line.id]: "" }));
    setActionError(null);
    markBusy([line.id], true);
    try {
      const res = await api.finalizeCatalogDraftsMany({
        draftIds: [draft.id],
        applySuggestions: true,
        ...(confirmDuplicateOf?.length ? { confirmDuplicateOf } : {}),
      });
      const r = res.results[0];
      if (r?.duplicateOf?.length) {
        setDuplicatePrompt({ line, draft, existing: r.duplicateOf });
        return;
      }
      if (!r?.itemId) {
        setLineErrors((p) => ({ ...p, [line.id]: r?.error ?? "Could not create the item" }));
        return;
      }
      setDuplicatePrompt(null);
      await Promise.all([refreshAll({ silent: true }), reloadItems()]);
    } catch (err: unknown) {
      setLineErrors((p) => ({
        ...p,
        [line.id]: err instanceof Error ? err.message : "Could not create the item",
      }));
    } finally {
      markBusy([line.id], false);
    }
  };

  /** Every open line with a category (chosen or suggested), in one pass; failures per line. */
  const createAll = async (targets: OpenLine[], confirmDuplicateOf?: string[]) => {
    if (targets.length === 0) return;
    const lineIds = targets.map((t) => t.line.id);
    setCreateAllBusy(true);
    setActionError(null);
    markBusy(lineIds, true);
    try {
      const res = await api.finalizeCatalogDraftsMany({
        draftIds: targets.map((t) => t.draft.id),
        applySuggestions: true,
        ...(confirmDuplicateOf?.length ? { confirmDuplicateOf } : {}),
      });
      const byDraft = new Map(res.results.map((r) => [r.draftId, r] as const));
      const held: Array<OpenLine & { existing: ExistingItemRef[] }> = [];
      const errors: Record<string, string> = {};
      for (const t of targets) {
        const r = byDraft.get(t.draft.id);
        if (r?.duplicateOf?.length) held.push({ ...t, existing: r.duplicateOf });
        else if (!r?.itemId) errors[t.line.id] = r?.error ?? "Could not create the item";
      }
      setDuplicateBatch(held);
      setLineErrors((p) => ({ ...p, ...errors }));
      if (res.promoted > 0) {
        await Promise.all([refreshAll({ silent: true }), reloadItems()]);
      }
      const failed = Object.keys(errors).length;
      if (failed > 0) {
        setActionError(
          `${res.promoted} item(s) created and linked. ${failed} line(s) failed — see the message under each line.`,
        );
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "Could not create the items");
    } finally {
      setCreateAllBusy(false);
      markBusy(lineIds, false);
    }
  };

  const createHeldBackAnyway = () => {
    const held = duplicateBatch;
    setDuplicateBatch([]);
    return createAll(
      held.map(({ line, draft }) => ({ line, draft })),
      held.flatMap((h) => h.existing.map((e) => e.id)),
    );
  };

  const saveLines = async () => {
    if (!id || !preview) return;
    setSaveBusy(true);
    setActionError(null);
    try {
      const updated = await api.patchInvoiceLines(id, {
        lines: preview.lines.map((ln) => ({
          lineId: ln.id,
          itemId: links[ln.id]?.itemId?.trim() ? links[ln.id]!.itemId.trim() : null,
          zoneId: links[ln.id]?.zoneId?.trim() ? links[ln.id]!.zoneId.trim() : null,
        })),
      });
      applyPreview(updated);
      await refreshAll({ silent: true });
    } catch (err: unknown) {
      setActionError(
        err instanceof Error ? err.message : "Could not save line links",
      );
    } finally {
      setSaveBusy(false);
    }
  };

  const commit = async () => {
    if (!id || !preview || !invoice) return;
    setCommitBusy(true);
    setActionError(null);
    try {
      if (!invoice.invoiceNumber?.trim()) {
        throw new Error(
          "Invoice number is missing. The PDF parser did not extract one — check the supplier PDF or pick another invoice.",
        );
      }
      const missingZone = preview.lines.some(
        (ln) => !links[ln.id]?.zoneId?.trim(),
      );
      const def = defaultZoneId.trim();
      if (missingZone && !def) {
        throw new Error(
          "Every line needs a receiving zone, or pick a default zone for lines without one.",
        );
      }

      const committed = await api.commitInvoice(id, {
        scheduledFor: new Date(scheduledFor).toISOString(),
        ...(def ? { defaultZoneId: def } : {}),
      });

      const deliveryId = committed.scheduledDelivery?.id;
      if (deliveryId) {
        navigate(`/app/deliveries/${deliveryId}`);
      } else {
        await refreshAll({ silent: true });
      }
    } catch (err: unknown) {
      setActionError(
        err instanceof Error ? err.message : "Could not create delivery",
      );
    } finally {
      setCommitBusy(false);
    }
  };

  const lineMoney = useCallback(
    (line: InvoicePreviewLine) =>
      `Qty ${line.quantity} × €${line.unitPriceEur} → €${line.lineTotalEur}${lineMoneyExtras(line)}`,
    [],
  );

  if (loading) {
    return (
      <section style={{ padding: "var(--space-4)" }}>Loading invoice…</section>
    );
  }

  if (error || !invoice) {
    return (
      <section
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        <div className="alert error">{error ?? "Invoice not found"}</div>
        <Link to="/app/deliveries" className="button plain">
          Back to deliveries
        </Link>
      </section>
    );
  }

  const label = invoice.invoiceNumber ?? invoice.originalFilename;
  const editable =
    invoice.status === "PARSED" || invoice.status === "NEEDS_REVIEW";
  const waitingParse =
    invoice.status === "UPLOADED" || invoice.status === "PARSING";
  // G-129: open = no item yet (stored, matched or picked); linked = the rest.
  const openLines = preview ? preview.lines.filter((ln) => !links[ln.id]?.itemId) : [];
  const linkedLines = preview ? preview.lines.filter((ln) => Boolean(links[ln.id]?.itemId)) : [];

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="card" style={{ padding: "var(--space-3)" }}>
        {pdfUrl ? (
          <>
            <InvoicePdfViewer key={pdfUrl} url={pdfUrl} />
            <a href={pdfUrl} download="invoice.pdf" className="button plain">Download PDF</a>
          </>
        ) : pdfError ? (
          <p role="alert">Could not load PDF: {pdfError}</p>
        ) : (
          <p role="status">Loading PDF…</p>
        )}
      </div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Invoice {label}</h2>
          <p className="page-subtitle">
            {invoice.supplier.name} · {invoice.location.name} (
            {invoice.location.code})
            {invoice.invoiceDate ? ` · dated ${calendarDayText(invoice.invoiceDate.slice(0, 10))}` : ""}
          </p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <span className="badge primary">{paperUseLabel(invoice.paperUse)}</span>
          <span className="badge muted">{invoice.status}</span>
          <DeletePaperButton
            invoiceId={invoice.id}
            label={`${invoice.supplier.name} ${label}`}
            status={invoice.status}
            hasDelivery={Boolean(invoice.scheduledDelivery)}
          />
        </div>
      </div>

      {actionError ? <div className="alert error">{actionError}</div> : null}
      {duplicateBatch.length > 0 ? (
        <div className="alert warning paper-lines__actions">
          <span>
            {duplicateBatch.length === 1
              ? `1 line was not created: an item called "${duplicateBatch[0].existing[0]?.name ?? ""}" already exists.`
              : `${duplicateBatch.length} lines were not created: items with the same names already exist (${duplicateBatch.slice(0, 3).map((h) => h.existing[0]?.name ?? "").filter(Boolean).join(", ")}${duplicateBatch.length > 3 ? ", …" : ""}).`}{" "}
            Pick the existing items on those lines, or create these anyway if they are really different products.
          </span>
          <button
            type="button"
            className="button danger"
            disabled={createAllBusy || busyLineIds.size > 0}
            onClick={() => void createHeldBackAnyway()}
          >
            Create {duplicateBatch.length === 1 ? "it" : `all ${duplicateBatch.length}`} anyway
          </button>
          <button type="button" className="button plain" onClick={() => setDuplicateBatch([])}>
            Dismiss
          </button>
        </div>
      ) : null}
      {duplicatePrompt ? (
        <DuplicateNameDialog
          name={duplicatePrompt.draft.name}
          existing={duplicatePrompt.existing}
          confirmLabel="Create it anyway"
          busy={busyLineIds.has(duplicatePrompt.line.id)}
          onConfirm={() =>
            void createAndLink(
              duplicatePrompt.line,
              duplicatePrompt.draft,
              duplicatePrompt.existing.map((e) => e.id),
            )
          }
          onClose={() => setDuplicatePrompt(null)}
        />
      ) : null}

      {waitingParse ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            Reading the paper and creating its items on the server… This page refreshes every few
            seconds. You can keep it open.
          </p>
        </div>
      ) : null}

      {invoice.status === "FAILED" ? (
        <div className="alert error">
          {invoice.parseError ?? "Invoice parsing failed."}
        </div>
      ) : null}

      <InvoiceParserReport payload={invoice.parsedPayload} />

      <div
        className="card"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
          background: "var(--color-surface-muted)",
        }}
      >
        <div style={{ fontSize: 14 }}>
          <div>
            <strong>Uploaded file</strong> {invoice.originalFilename}
          </div>
          <div className="muted" style={{ marginTop: "var(--space-2)" }}>
            Parser {invoice.parserSupplierCode ?? "—"} · v
            {invoice.parserVersion ?? "—"}
          </div>
          {/* G-121 S1: what the paper prints in its totals block, beside the
              net line figures. A figure the parser did not read is a dash,
              never €0.00 — an unknown must look unknown. */}
          <div className="inv-money-block">
            <strong>Document totals</strong>
            <div className="inv-money-grid">
              <div>
                <small className="muted">Base (before IVA)</small>
                <div>{money(invoice.baseEur)}</div>
              </div>
              <div>
                <small className="muted">IVA</small>
                <div>{money(invoice.ivaEur)}</div>
              </div>
              <div>
                <small className="muted">Total (with IVA)</small>
                <div>{money(invoice.grandTotalEur)}</div>
              </div>
              <div>
                <small className="muted">Due</small>
                <div>{invoice.dueDate ? invoice.dueDate.slice(0, 10) : "—"}</div>
              </div>
            </div>
            {showsRateRows(invoice.vatBreakdown)
              ? invoice.vatBreakdown.map((rate) => (
                  <div key={rate.id}>
                    {percentText(rate.ratePercent)} %: base {money(rate.baseEur)} · IVA{" "}
                    {money(rate.ivaEur)}
                  </div>
                ))
              : null}
            {moneySourceNote(invoice.moneySource) ? (
              <div className="muted">{moneySourceNote(invoice.moneySource)}</div>
            ) : null}
          </div>
          {/* G-121 S4: what the paper is for; on a factura, the purchases
              already typed by hand and (owner only) its paid state. */}
          {/* G-164: a factura's number and date, typed when the reader missed them. */}
          {(isBill(invoice.paperUse) || isCreditNote(invoice.paperUse)) && invoice.status !== "FAILED" && !waitingParse ? (
            <FacturaDetailsBlock invoice={invoice} onChanged={() => void refreshAll({ silent: true })} />
          ) : null}
          <PaperUseBlock invoice={invoice} onChanged={setInvoice} />
          {isBill(invoice.paperUse) ? <HandTypedPurchasesHint invoiceId={invoice.id} /> : null}
          {isOwner && isBill(invoice.paperUse) && invoice.status !== "FAILED" && !waitingParse ? (
            <InvoicePaymentsBlock key={`payments-${creditTick}`} invoiceId={invoice.id} />
          ) : null}
          {/* G-131: which factura counts this albarán, or what this factura bills. */}
          {invoice.paperUse !== "ITEMS_ONLY" && invoice.status !== "FAILED" && !waitingParse ? (
            <PaperCoverageBlock invoice={invoice} onChanged={() => void refreshAll({ silent: true })} />
          ) : null}
          {/* G-173: a credit note and the factura it corrects — billed, credited, to pay. */}
          {invoice.status !== "FAILED" && !waitingParse ? (
            <CreditLinkBlock
              invoice={invoice}
              onChanged={() => {
                setCreditTick((t) => t + 1);
                void refreshAll({ silent: true });
              }}
            />
          ) : null}
          {invoice.parseError && invoice.status !== "FAILED" ? (
            <div className="alert error" style={{ marginTop: "var(--space-3)" }}>
              {invoice.parseError}
            </div>
          ) : null}
        </div>
      </div>

      {editable && preview ? (
        <>
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <h3 className="inv-section-title">Line items ({preview.lines.length})</h3>
            {/* G-129 (OD-159): the app created an item for every line whose
                category the reader knew when the paper was read; what is left
                is a decision for a person. */}
            {preview.autoCreatedCount > 0 ? (
              <p className="muted inv-section-note">
                The app created {preview.autoCreatedCount === 1 ? "1 item" : `${preview.autoCreatedCount} items`} from this
                paper by itself when it was read.
              </p>
            ) : null}
            {openLines.length > 0 ? (
              <PaperOpenLinesTable
                lines={openLines}
                links={links}
                categories={categoriesSorted}
                colors={colors}
                items={itemsSorted}
                zones={zonesSorted}
                busyLineIds={busyLineIds}
                lineErrors={lineErrors}
                createAllBusy={createAllBusy}
                schedules={canSchedule(invoice.paperUse)}
                onPatchDraft={patchDraft}
                onPhotoDraft={photoDraft}
                onCreate={(line, draft) => createAndLink(line, draft)}
                onCreateAll={(targets) => createAll(targets)}
                onPickItem={(lineId, itemId) => setLink(lineId, { itemId })}
                onPickZone={(lineId, zoneId) => setLink(lineId, { zoneId })}
              />
            ) : (
              <p className="muted inv-section-note">Every line has an item.</p>
            )}
            {linkedLines.length > 0 ? (
              <>
                <h4 className="paper-lines__subtitle">Linked ({linkedLines.length})</h4>
                <PaperLinkedLinesList
                  lines={linkedLines}
                  links={links}
                  items={itemsSorted}
                  zones={zonesSorted}
                  lineMoney={lineMoney}
                  onPickItem={(lineId, itemId) => setLink(lineId, { itemId })}
                  onPickZone={(lineId, zoneId) => setLink(lineId, { zoneId })}
                />
              </>
            ) : null}
            <div className="paper-lines__actions">
              <button
                type="button"
                className="button tinted"
                disabled={saveBusy || createAllBusy}
                onClick={() => void saveLines()}
              >
                {saveBusy ? "Saving…" : "Save line links"}
              </button>
            </div>
          </div>

          {/* G-131: the server says why this paper cannot become a delivery now
              (`paper-rules.ts` scheduleBlock) — the same sentence commit refuses with. */}
          {preview.coverage.scheduleBlock ? (
            <div className="card">
              <h3 className="inv-section-title">Schedule delivery</h3>
              <p className="muted inv-section-note">{preview.coverage.scheduleBlock.reason}</p>
            </div>
          ) : (
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <h3 style={{ margin: 0 }}>Schedule delivery</h3>
            <p className="muted" style={{ margin: 0, fontSize: 14 }}>
              Commit turns this invoice into a scheduled delivery (expected quantities and costs come
              from the parsed lines). Lines without a zone use the default receiving zone below.
            </p>
            {!invoice.invoiceNumber?.trim() ? (
              <div className="alert">
                {isBill(invoice.paperUse)
                  ? "No number yet — type the factura's number above before scheduling."
                  : "Invoice number is missing — commit will be rejected until the parser supplies one."}
              </div>
            ) : null}
            <div className="field">
              <label htmlFor="inv-eta">Expected arrival</label>
              <input
                id="inv-eta"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="inv-default-zone">Default receiving zone (optional)</label>
              <select
                id="inv-default-zone"
                value={defaultZoneId}
                onChange={(e) => setDefaultZoneId(e.target.value)}
              >
                <option value="">— None —</option>
                {zonesSorted.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="paper-lines__actions">
              <button
                type="button"
                className="button"
                disabled={commitBusy || openLines.length > 0}
                title={openLines.length > 0 ? "Every line needs an item first" : undefined}
                onClick={() => void commit()}
              >
                {commitBusy ? "Committing…" : "Commit and open delivery"}
              </button>
              {openLines.length > 0 ? (
                <span className="muted inv-section-note">
                  {openLines.length === 1 ? "1 line still needs an item." : `${openLines.length} lines still need an item.`}
                </span>
              ) : null}
            </div>
          </div>
          )}
        </>
      ) : null}

      {editable && !preview ? (
        <div className="card">Loading preview…</div>
      ) : null}

      {!editable && invoice.status !== "FAILED" && !waitingParse ? (
        <div className="card">
          <h3 style={{ margin: "0 0 var(--space-3)" }}>
            Lines ({invoice.lines.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {invoice.lines.slice(0, 80).map((line) => (
              <div
                key={line.id}
                style={{
                  padding: "var(--space-3)",
                  borderRadius: "var(--radius-md)",
                  background: "var(--color-surface-muted)",
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {line.supplierSku ?? "—"} · {line.descriptionRaw}
                </div>
                <div className="muted" style={{ marginTop: "var(--space-1)" }}>
                  Qty {line.quantity} × €{line.unitPriceEur} → €{line.lineTotalEur}
                  {lineMoneyExtras(line)}
                </div>
                {invoice.status === "COMMITTED" ? (
                  <div className="muted" style={{ marginTop: "var(--space-1)" }}>
                    Item {itemLabel(line.itemId)} · Zone {zoneLabel(line.zoneId)}
                  </div>
                ) : null}
              </div>
            ))}
            {invoice.lines.length > 80 ? (
              <p className="muted" style={{ margin: 0 }}>
                Showing first 80 lines.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <Link to="/app/deliveries" className="button plain">
        Back to deliveries
      </Link>
    </section>
  );
}
